import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  StatusBar,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useToast } from '@/contexts/admin/ToastContext';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { confirmationStrings, toastStrings } from '@/constants/residents/marketplaceStrings';
import {
  MyListing,
  getConditionDisplayText,
  getPickupOptionDisplayText,
  myListingToFormData,
} from '@/types/residents/marketplaceListing';
import {
  PhotoGalleryModal,
  ConfirmationModal,
  PostItemModal,
  ImageCarousel,
  StatItem,
  SoldFeedbackModal,
  ListingConversationsList,
} from '@/components/residents/marketplace';
import { ListingBuyer } from '@/types/residents/marketplaceListing';
import { SVG_COMPONENTS } from '@/assets/admin';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { calculateCarouselDimensions } from '@/utils/marketplace';
import {
  useMarkAsSoldMutation,
  useDeleteListingMutation,
  useRepostListingMutation,
  useLazyGetMyListingsQuery,
  useLazyGetListingBuyersQuery,
  useCreateFeedbackRequestMutation,
} from '@/store/api';
import { GradientBackground } from '@/components/residents/redesign';
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { BackLink } from '@/components/residents/mobile/BackLink';

type MyListingItemDetailNavigationProp = StackNavigationProp<RootStackParamList, 'MyListingItemDetail'>;
type MyListingItemDetailRouteProp = RouteProp<RootStackParamList, 'MyListingItemDetail'>;

const PencilIcon = SVG_COMPONENTS.PENCIL;
const TrashIcon = SVG_COMPONENTS.TRASH;
const DeliveryIcon = RESIDENTS_SVG_COMPONENTS.DELIVERY;

const HP = REDESIGN_SPACING.horizontalPadding;
const MAX_CONTENT_WIDTH = 1200;

export const MyListingItemDetailScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation<MyListingItemDetailNavigationProp>();
  const route = useRoute<MyListingItemDetailRouteProp>();
  const { layoutMode, width: screenWidth, isWeb } = useResponsive();
  const { showSuccess, showError } = useToast();

  const [markAsSold, { isLoading: isMarkingSold }] = useMarkAsSoldMutation();
  const [deleteListing, { isLoading: isDeleting }] = useDeleteListingMutation();
  const [repostListing, { isLoading: isReposting }] = useRepostListingMutation();
  const [getMyListings] = useLazyGetMyListingsQuery();
  const [getListingBuyers] = useLazyGetListingBuyersQuery();
  const [createFeedbackRequest] = useCreateFeedbackRequestMutation();

  const isMobile = layoutMode === 'mobile';
  const isDesktop = layoutMode === 'desktop';

  const carouselDimensions = calculateCarouselDimensions(layoutMode, screenWidth);

  const { listing } = route.params;
  const [currentListing, setCurrentListing] = useState<MyListing>(listing);
  const [isGalleryVisible, setIsGalleryVisible] = useState(false);
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isMarkSoldModalVisible, setIsMarkSoldModalVisible] = useState(false);
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false);
  const [isFeedbackModalVisible, setIsFeedbackModalVisible] = useState(false);
  const [listingBuyers, setListingBuyers] = useState<ListingBuyer[]>([]);

  useEffect(() => {
    const fetchFreshListing = async () => {
      try {
        const result = await getMyListings({ status: 'all' }).unwrap();
        const freshListing = result.find(l => l.id === listing.id);
        if (freshListing) setCurrentListing(freshListing);
      } catch (error) { console.warn('Failed to fetch fresh listing data:', error); }
    };
    fetchFreshListing();
  }, [listing.id, getMyListings]);

  const isActive = currentListing.status === 'active';

  const formatSoldDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleImagePress = (index: number) => { setGalleryInitialIndex(index); setIsGalleryVisible(true); };
  const handleCloseGallery = () => setIsGalleryVisible(false);

  const handleEditPress = () => {
    if (isMobile) {
      navigation.navigate('PostItem', { initialData: myListingToFormData(currentListing), isEditMode: true, listingId: currentListing.id });
    } else {
      setIsEditModalVisible(true);
    }
  };
  const handleCloseEditModal = () => setIsEditModalVisible(false);

  const handleEditSuccess = async () => {
    setIsEditModalVisible(false);
    showSuccess(toastStrings.listingUpdated);
    try {
      const result = await getMyListings({ status: 'all' }).unwrap();
      const updatedListing = result.find(l => l.id === currentListing.id);
      if (updatedListing) setCurrentListing(updatedListing);
    } catch (error) { console.warn('Failed to refetch listing after edit:', error); }
  };

  useEffect(() => {
    const unsubscribe = navigation.addListener('focus', async () => {
      try {
        const result = await getMyListings({ status: 'all' }).unwrap();
        const freshListing = result.find(l => l.id === listing.id);
        if (freshListing) setCurrentListing(freshListing);
      } catch (error) { console.warn('Failed to refetch listing data:', error); }
    });
    return unsubscribe;
  }, [navigation, listing.id, getMyListings]);

  const handleMarkSoldPress = () => setIsMarkSoldModalVisible(true);
  const handleMarkSoldCancel = () => setIsMarkSoldModalVisible(false);

  const handleMarkSoldProceed = async () => {
    try {
      await markAsSold(currentListing.id).unwrap();
      setIsMarkSoldModalVisible(false);
      try {
        const buyers = await getListingBuyers(currentListing.id).unwrap();
        if (buyers && buyers.length > 0) {
          setListingBuyers(buyers);
          setIsFeedbackModalVisible(true);
        } else {
          showSuccess(toastStrings.itemMarkedSold);
          navigation.navigate('MarketplaceProfile', { action: 'sold', listingId: currentListing.id });
        }
      } catch (buyersError) {
        console.warn('Failed to fetch listing buyers:', buyersError);
        showSuccess(toastStrings.itemMarkedSold);
        navigation.navigate('MarketplaceProfile', { action: 'sold', listingId: currentListing.id });
      }
    } catch (error) {
      setIsMarkSoldModalVisible(false);
      showError(toastStrings.markSoldFailed);
    }
  };

  const handleFeedbackModalClose = () => {
    setIsFeedbackModalVisible(false);
    setListingBuyers([]);
    showSuccess(toastStrings.itemMarkedSold);
    navigation.navigate('MarketplaceProfile', { action: 'sold', listingId: currentListing.id });
  };

  const handleFeedbackSubmit = async (buyerId: string | null) => {
    if (buyerId) {
      try { await createFeedbackRequest({ listingId: currentListing.id, buyerId }).unwrap(); }
      catch (error) { console.warn('Failed to create feedback request:', error); }
    }
    setIsFeedbackModalVisible(false);
    setListingBuyers([]);
    showSuccess(toastStrings.itemMarkedSold);
    navigation.navigate('MarketplaceProfile', { action: 'sold', listingId: currentListing.id });
  };

  const handlePostAgainPress = async () => {
    try {
      await repostListing(currentListing.id).unwrap();
      showSuccess(toastStrings.itemPostedAgain);
      navigation.navigate('MarketplaceProfile', { action: 'reposted', listingId: currentListing.id });
    } catch (error) { showError(toastStrings.repostFailed); }
  };

  const handleRemovePress = () => setIsRemoveModalVisible(true);
  const handleRemoveCancel = () => setIsRemoveModalVisible(false);

  const handleRemoveProceed = async () => {
    try {
      await deleteListing(currentListing.id).unwrap();
      setIsRemoveModalVisible(false);
      showSuccess(toastStrings.itemRemoved);
      navigation.navigate('MarketplaceProfile', { action: 'removed', listingId: currentListing.id });
    } catch (error) {
      setIsRemoveModalVisible(false);
      showError(toastStrings.removeFailed);
    }
  };

  const scrollContent = (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={[styles.content, isDesktop && styles.contentDesktop]}
      showsVerticalScrollIndicator={false}
    >
      <View style={[styles.contentWrapper, isDesktop && styles.contentWrapperDesktop]}>
        <BackLink label="Back to my profile" onPress={() => navigation.goBack()} color={PRIMITIVE_COLORS.resioBlue} />
        <View style={styles.itemSection}>
          <View style={styles.topSection}>
            <View style={!isMobile && styles.carouselContainer}>
              <ImageCarousel
                images={currentListing.images.map(img => img.url)}
                onImagePress={handleImagePress}
                width={!isMobile ? carouselDimensions.width : undefined}
                height={!isMobile ? carouselDimensions.height : undefined}
                layoutMode={layoutMode}
                showArrows={!isMobile}
              />
            </View>

            <View style={[styles.statsActionsRow, { borderBottomColor: colors.separator }]}>
              {isActive ? (
                <View style={styles.statsContainer}>
                  <StatItem value={currentListing.views} label="Views" />
                  <StatItem value={currentListing.saved} label="Saved" />
                </View>
              ) : (
                <View style={styles.soldInfoContainer}>
                  <Text style={[styles.soldLabel, { color: colors.text.muted }]}>Sold</Text>
                  <Text style={[styles.soldDate, { color: colors.text.primary }]}>{formatSoldDate(currentListing.soldAt)}</Text>
                </View>
              )}

              <View style={styles.actionsContainer}>
                <TouchableOpacity style={styles.editButton} onPress={handleEditPress} activeOpacity={0.8}>
                  <PencilIcon width={19} height={19} color={PRIMITIVE_COLORS.resioBlue} />
                </TouchableOpacity>

                {isActive ? (
                  <TouchableOpacity
                    style={[styles.primaryActionButton, isMarkingSold && styles.primaryActionButtonDisabled]}
                    onPress={handleMarkSoldPress}
                    activeOpacity={0.8}
                    disabled={isMarkingSold}
                  >
                    {isMarkingSold ? (
                      <ActivityIndicator size="small" color={colors.text.primary} />
                    ) : (
                      <Text style={[styles.primaryActionButtonText, { color: colors.text.primary }]}>Mark as sold</Text>
                    )}
                  </TouchableOpacity>
                ) : (
                  <TouchableOpacity
                    style={[styles.primaryActionButton, isReposting && styles.primaryActionButtonDisabled]}
                    onPress={handlePostAgainPress}
                    activeOpacity={0.8}
                    disabled={isReposting}
                  >
                    {isReposting ? (
                      <ActivityIndicator size="small" color={colors.text.primary} />
                    ) : (
                      <Text style={[styles.primaryActionButtonText, { color: colors.text.primary }]}>Post again</Text>
                    )}
                  </TouchableOpacity>
                )}
              </View>
            </View>
          </View>

          <View style={styles.detailsSection}>
            <View style={styles.priceConditionRow}>
              <Text style={[styles.price, { color: colors.text.primary }]}>{currentListing.price}</Text>
              <Text style={[styles.condition, { color: colors.text.muted }]}>{getConditionDisplayText(currentListing.condition)}</Text>
            </View>
            <Text style={[styles.title, { color: colors.text.primary }]}>{currentListing.title}</Text>
            <Text style={[styles.description, { color: colors.text.muted }]}>{currentListing.description}</Text>
            <View style={[styles.pickupBadge, { backgroundColor: colors.glass.buttonBg }]}>
              <DeliveryIcon width={16} height={16} color={colors.text.muted} />
              <Text style={[styles.pickupText, { color: colors.text.muted }]}>{getPickupOptionDisplayText(currentListing.pickupOption)}</Text>
            </View>
          </View>

          {isActive && <ListingConversationsList listingId={currentListing.id} />}

          <TouchableOpacity
            style={[styles.removeButton, { borderTopColor: colors.separator }, isDeleting && styles.removeButtonDisabled]}
            onPress={handleRemovePress}
            activeOpacity={0.8}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <ActivityIndicator size="small" color={colors.accent.red} />
            ) : (
              <>
                <TrashIcon width={20} height={20} color={colors.accent.red} />
                <Text style={[styles.removeButtonText, { color: colors.accent.red }]}>Remove listing</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </ScrollView>
  );

  const modals = (
    <>
      <PhotoGalleryModal
        visible={isGalleryVisible}
        onClose={handleCloseGallery}
        images={currentListing.images.map(img => img.url)}
        initialIndex={galleryInitialIndex}
        onContactSeller={() => {}}
        onReport={() => {}}
      />
      {!isMobile && (
        <PostItemModal
          visible={isEditModalVisible}
          onClose={handleCloseEditModal}
          onSubmitSuccess={handleEditSuccess}
          initialData={myListingToFormData(currentListing)}
          isEditMode
          listingId={currentListing.id}
        />
      )}
      <ConfirmationModal
        visible={isMarkSoldModalVisible}
        message={confirmationStrings.markAsSoldConfirmMessage}
        onCancel={handleMarkSoldCancel}
        onProceed={handleMarkSoldProceed}
      />
      <ConfirmationModal
        visible={isRemoveModalVisible}
        message={confirmationStrings.removeListingMessage}
        onCancel={handleRemoveCancel}
        onProceed={handleRemoveProceed}
      />
      <SoldFeedbackModal
        visible={isFeedbackModalVisible}
        buyers={listingBuyers}
        onClose={handleFeedbackModalClose}
        onSubmit={handleFeedbackSubmit}
      />
    </>
  );

  if (isDesktop) {
    return (
      <>
        <View style={styles.desktopContainer}>{scrollContent}</View>
        {modals}
      </>
    );
  }

  return (
    <>
      <GradientBackground>
        <StatusBar barStyle="light-content" />
        <BaseResidentMobileScreen screenName="Marketplace" darkBackground headerVariant="gradient">
          {scrollContent}
        </BaseResidentMobileScreen>
      </GradientBackground>
      {modals}
    </>
  );
};

const styles = StyleSheet.create({
  desktopContainer: { flex: 1, backgroundColor: 'transparent' },
  scrollView: { flex: 1 },
  content: { padding: HP, paddingBottom: 40, gap: 24 },
  contentDesktop: { alignItems: 'center', paddingBottom: 60 },
  contentWrapper: { gap: 24, width: '100%' },
  contentWrapperDesktop: { maxWidth: MAX_CONTENT_WIDTH },
  itemSection: { gap: 24 },
  topSection: { gap: 20 },
  carouselContainer: { alignItems: 'center' },
  statsActionsRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 20, borderBottomWidth: 1 },
  statsContainer: { flexDirection: 'row', gap: 20 },
  soldInfoContainer: { alignItems: 'flex-start' },
  soldLabel: { fontSize: 14, fontWeight: '400', lineHeight: 21 },
  soldDate: { fontSize: 14, fontWeight: '400', lineHeight: 21 },
  actionsContainer: { flexDirection: 'row', gap: 16, alignItems: 'center' },
  editButton: { width: 34, height: 34, borderWidth: 1, borderColor: PRIMITIVE_COLORS.resioBlue, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  primaryActionButton: { height: 34, backgroundColor: PRIMITIVE_COLORS.resioBlue, borderRadius: 8, paddingHorizontal: 16, alignItems: 'center', justifyContent: 'center' },
  primaryActionButtonText: { fontSize: 12, fontWeight: '500', lineHeight: 18 },
  primaryActionButtonDisabled: { opacity: 0.6 },
  detailsSection: { gap: 16 },
  priceConditionRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  price: { fontSize: 24, fontWeight: '700', lineHeight: 36 },
  condition: { fontSize: 12, fontWeight: '400', lineHeight: 18, textAlign: 'right' },
  title: { fontSize: 16, fontWeight: '600', lineHeight: 24 },
  description: { fontSize: 14, fontWeight: '400', lineHeight: 21 },
  pickupBadge: { flexDirection: 'row', alignItems: 'center', gap: 6, borderRadius: 20, paddingHorizontal: 12, height: 28, alignSelf: 'flex-start' },
  pickupText: { fontSize: 12, fontWeight: '500', lineHeight: 18 },
  removeButton: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10, paddingVertical: 20, borderTopWidth: 1 },
  removeButtonText: { fontSize: 14, fontWeight: '500', lineHeight: 21 },
  removeButtonDisabled: { opacity: 0.6 },
});
